'use client';

import { chevronLeftIcon, chevronRightIcon, sortArrow, threeDIcon } from '../../config/icons';
import { TProject, ApartmentEntry } from '../../types/config';
import { useIsMobile } from '../../hooks';
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { getInventoryConfig } from './config';
import { InventoryRow, InventoryRowColValue } from './types';
import { LandingSectionTitle } from '../landing-section-title';
import { ProjectDealType } from '../../types/core';

// show sort arrow, or empty box to keep design
const SortDirIcon: React.FC<{
  visible?: boolean;
  asc?: boolean;
  size?: string;
}> = ({ asc, visible, size }) => {
  return visible ? (
    <Stack
      sx={{
        transform: asc ? 'rotate(0deg)' : 'rotate(180deg)',
        '& svg': {
          width: size || '16px',
          height: size || '16px',
        },
      }}
      alignItems="center"
    >
      {sortArrow}
    </Stack>
  ) : (
    <Box width={size || '16px'} />
  );
};

export type InventoryProps = {
  project: TProject;
  aptArray?: ApartmentEntry[];
  onRowClick?: (row: InventoryRow) => void;
  onRowImageClick?: (e: React.MouseEvent, row: InventoryRow) => void;
  t: (key: string, options?: any) => string;
  lang: string;
  isWizardPreview?: boolean;
};

export function Inventory({
  project,
  aptArray,
  onRowClick,
  onRowImageClick,
  t,
  lang,
  isWizardPreview = false,
}: InventoryProps) {
  const isMobile = useIsMobile();
  const { direction, palette } = useTheme();
  const [loadingItemId, setLoadingItemId] = useState<number>();

  const [sortAsc, setSortAsc] = useState<boolean>(false);
  const [sortCol, setSortCol] = useState<InventoryRowColValue>(InventoryRowColValue.Order);

  const [rowsData, withRooms, withLivingAreas, withFloors, withPrices] = useMemo(
    () => getInventoryConfig(t, project, aptArray, isWizardPreview),
    [aptArray, project],
  );

  const handleSort = useCallback(
    (col: InventoryRowColValue) => {
      let newSortAsc = sortAsc;
      let newSortCol = sortCol;
      if (col === sortCol) {
        newSortAsc = !sortAsc;
      } else {
        newSortCol = col;
        newSortAsc = true;
      }
      setSortAsc(newSortAsc);
      setSortCol(newSortCol);
    },
    [sortAsc, sortCol],
  );

  const rows = useMemo(() => {
    if (sortAsc) {
      if (sortCol === InventoryRowColValue.Type) {
        // sort string using locale
        rowsData.sort((a, b) =>
          a[sortCol].localeCompare(b[sortCol], lang, { sensitivity: 'base' }),
        );
      } else {
        rowsData.sort((a, b) => Number(b[sortCol]) - Number(a[sortCol]));
      }
    } else {
      if (sortCol === InventoryRowColValue.Type) {
        // sort string using locale
        rowsData.sort((a, b) =>
          b[sortCol].localeCompare(a[sortCol], lang, { sensitivity: 'base' }),
        );
      } else {
        rowsData.sort((a, b) => Number(a[sortCol]) - Number(b[sortCol]));
      }
    }
    return rowsData;
  }, [lang, rowsData, sortAsc, sortCol]);

  const handleRowClick = (row: InventoryRow) => {
    if (!!row.tourUrl) {
      setLoadingItemId(row.listingId);
      // Reset loading state after 5 seconds
      setTimeout(() => {
        setLoadingItemId(undefined);
      }, 5000);
    }

    onRowClick && onRowClick(row);
  };

  if (!rows.length) return null;

  return (
    <>
      <Stack spacing="20px" mx={{ xs: '16px', sm: '0' }}>
        <LandingSectionTitle
          title={
            project.dealType === ProjectDealType.Rent
              ? t('labels.inventorySectionTitleRent')
              : t('labels.inventorySectionTitle')
          }
        />

        <TableContainer
          component={Paper}
          elevation={1}
          sx={{
            borderRadius: '12px',
            border: '1px solid #EAECF0',
          }}
        >
          <Table>
            <TableHead
              sx={{
                height: isWizardPreview ? '24px' : '44px',
                borderBottom: '1px solid #EAECF0',
              }}
            >
              <TableRow
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '.MuiTableCell-root': {
                    padding: '6px 0',
                    paddingLeft: '16px',
                    backgroundColor: 'grey.50',
                  },
                }}
              >
                <TableCell
                  align="left"
                  onClick={() => {
                    handleSort(InventoryRowColValue.Type);
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing="4px">
                    <Typography
                      variant="caption"
                      fontWeight={500}
                      fontSize={isWizardPreview ? '10px' : '12px'}
                    >
                      {t('labels.type')}
                    </Typography>
                    <SortDirIcon
                      visible={sortCol === InventoryRowColValue.Type}
                      asc={sortAsc}
                      size={isWizardPreview ? '12px' : '16px'}
                    />
                  </Stack>
                </TableCell>
                {withRooms && !isMobile && (
                  <TableCell
                    align="left"
                    onClick={() => {
                      handleSort(InventoryRowColValue.Rooms);
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing="4px">
                      <Typography
                        variant="caption"
                        fontWeight={500}
                        fontSize={isWizardPreview ? '10px' : '12px'}
                      >
                        {t('labels.rooms')}
                      </Typography>
                      <SortDirIcon
                        visible={sortCol === InventoryRowColValue.Rooms}
                        asc={sortAsc}
                        size={isWizardPreview ? '12px' : '16px'}
                      />
                    </Stack>
                  </TableCell>
                )}
                {withLivingAreas && !isMobile && (
                  <TableCell
                    align="left"
                    onClick={() => {
                      handleSort(InventoryRowColValue.LivingAreas);
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing="4px">
                      <Typography
                        variant="caption"
                        fontWeight={500}
                        fontSize={isWizardPreview ? '10px' : '12px'}
                      >
                        {t('labels.area')}
                      </Typography>
                      <SortDirIcon
                        visible={sortCol === InventoryRowColValue.LivingAreas}
                        asc={sortAsc}
                        size={isWizardPreview ? '12px' : '16px'}
                      />
                    </Stack>
                  </TableCell>
                )}
                {withFloors && !isMobile && (
                  <TableCell
                    align="left"
                    onClick={() => {
                      handleSort(InventoryRowColValue.Floors);
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing="4px">
                      <Typography
                        variant="caption"
                        fontWeight={500}
                        fontSize={isWizardPreview ? '10px' : '12px'}
                      >
                        {t('labels.floors')}
                      </Typography>
                      <SortDirIcon
                        visible={sortCol === InventoryRowColValue.Floors}
                        asc={sortAsc}
                        size={isWizardPreview ? '12px' : '16px'}
                      />
                    </Stack>
                  </TableCell>
                )}
                {withPrices && !isMobile && (
                  <TableCell
                    align="left"
                    onClick={() => {
                      handleSort(InventoryRowColValue.Prices);
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing="4px">
                      <Typography
                        variant="caption"
                        fontWeight={500}
                        fontSize={isWizardPreview ? '10px' : '12px'}
                      >
                        {t('labels.price')}
                      </Typography>
                      <SortDirIcon
                        visible={sortCol === InventoryRowColValue.Prices}
                        asc={sortAsc}
                        size={isWizardPreview ? '12px' : '16px'}
                      />
                    </Stack>
                  </TableCell>
                )}
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row, idx) => (
                <TableRow
                  key={idx}
                  onClick={() => {
                    if (!loadingItemId) {
                      // Only allow clicks if no item is loading
                      handleRowClick(row);
                    }
                  }}
                  sx={{
                    height: '48px',
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #EAECF0',
                    opacity:
                      loadingItemId !== undefined &&
                      (row.listingId || row.polygonId) !== undefined &&
                      loadingItemId !== (row.listingId || row.polygonId)
                        ? 0.5
                        : 1,
                    pointerEvents:
                      loadingItemId !== undefined &&
                      (row.listingId || row.polygonId) !== undefined &&
                      loadingItemId !== (row.listingId || row.polygonId)
                        ? 'none'
                        : 'auto',
                    '.MuiTableCell-root': {
                      paddingY: 1,
                    },
                    '#chevron-right': {
                      transition: 'all 0.3s',
                      transform: 'translateX(0)',
                    },
                    '&:hover': {
                      backgroundColor: 'grey.100',
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      '#chevron-right': {
                        transform: 'translateX(10px)',
                      },
                    },
                    '&:last-child td, &:last-child th': { border: 0 },
                  }}
                >
                  <TableCell align="left">
                    <Stack direction="row" spacing={isWizardPreview ? 1 : 2}>
                      <Stack position="relative" alignItems="center" justifyContent="center">
                        {(row.tourPreviewUrl || project.mainImageUrl) && (
                          <img
                            src={(row.tourPreviewUrl || project.mainImageUrl) as string}
                            width={isWizardPreview ? 28 : 40}
                            height={isWizardPreview ? 28 : 40}
                            style={{
                              borderRadius: '8px',
                              opacity:
                                !isWizardPreview && loadingItemId === row.polygonId ? 0.5 : 1,
                            }}
                            onClick={(e) => {
                              onRowImageClick && onRowImageClick(e, row);
                            }}
                          />
                        )}
                        <CircularProgress
                          size={22}
                          sx={{
                            position: 'absolute',
                            zIndex: 1,
                            display:
                              !isWizardPreview && loadingItemId === row.polygonId
                                ? 'block'
                                : 'none',
                          }}
                        />
                      </Stack>
                      <Stack direction="column" justifyContent={isMobile ? 'flex-start' : 'center'}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={isWizardPreview ? 1 : 2}
                        >
                          <Typography
                            variant={isWizardPreview ? 'caption' : 'body1'}
                            fontWeight={500}
                          >
                            {row.title}
                          </Typography>
                          {row.tourUrl && (
                            <SvgIcon
                              sx={{
                                width: isWizardPreview ? '25px' : '33px',
                                height: isWizardPreview ? '15px' : '20px',
                                background: palette.grey[100],
                                padding: '4px 6px',
                                borderRadius: '20px',
                              }}
                            >
                              {threeDIcon}
                            </SvgIcon>
                          )}
                        </Stack>
                        {isMobile && (
                          <>
                            {withRooms && (
                              <Typography variant="button" fontWeight={400} color="grey.400">
                                {t('labels.rooms')}: {row.roomsOutput}
                              </Typography>
                            )}
                            {withLivingAreas && (
                              <Typography variant="button" fontWeight={400} color="grey.400">
                                {t('labels.area')}: {row.livingAreasOutput}
                              </Typography>
                            )}
                            {withFloors && (
                              <Typography variant="button" fontWeight={400} color="grey.400">
                                {t('labels.floor')}: {row.floorsOutput}
                              </Typography>
                            )}
                            {withPrices && (
                              <Typography variant="button" fontWeight={400} color="grey.400">
                                {t('labels.price')}: {row.pricesOutput}
                              </Typography>
                            )}
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </TableCell>

                  {withRooms && !isMobile && (
                    <TableCell align="left">
                      <Typography variant={isWizardPreview ? 'caption' : 'body1'} fontWeight={500}>
                        {row.roomsOutput}
                      </Typography>
                    </TableCell>
                  )}
                  {withLivingAreas && !isMobile && (
                    <TableCell align="left">
                      <Typography variant={isWizardPreview ? 'caption' : 'body1'} fontWeight={500}>
                        {row.livingAreasOutput}
                      </Typography>
                    </TableCell>
                  )}
                  {withFloors && !isMobile && (
                    <TableCell align="left">
                      <Typography
                        variant={isWizardPreview ? 'caption' : 'body1'}
                        fontWeight={500}
                        dir="ltr"
                      >
                        {row.floorsOutput}
                      </Typography>
                    </TableCell>
                  )}
                  {withPrices && !isMobile && (
                    <TableCell align="left">
                      <Typography variant={isWizardPreview ? 'caption' : 'body1'} fontWeight={500}>
                        {row.pricesOutput}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell width="70px" align="right">
                    <SvgIcon id="chevron-right" sx={{ height: 16, mt: 0.5, color: 'grey.500' }}>
                      {direction === 'rtl' ? chevronLeftIcon : chevronRightIcon}
                    </SvgIcon>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </>
  );
}
